import { formatClockTime } from '@/shared/lib/time-format';
import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { PatientAvatar } from '@/shared/ui/components';
import {
  useProviderSlots,
  type ProviderSlotsResponse,
} from '@/services/scheduling/use-provider-slots';
import { filterProvidersForCurrentUser } from '@/scheduling/lib/provider-self-filter';
import { useAuthStore } from '@/auth/auth-store';
import type { AppointmentSlot, AppointmentValueSets } from '@/types/scheduling-api';

/**
 * RecurrenceReselectDoctorView — PROD-1011 "Select another doctor" sub-view.
 *
 * Re-enters provider selection for a SINGLE recurring occurrence (Flutter
 * `select_doctor_widget.dart`, which runs its OWN `internalBloc` — isolated
 * from the main appointment state). Lists providers with inline slot previews
 * for the chosen day; picking a slot returns `(provider, slot)` via `onPick`.
 * Never writes the main appointment form.
 *
 * Shares the create-flow wire call (`useProviderSlots`, `searchSlot: true`).
 * The picked slot is stamped with the chosen provider/practice context — that
 * `providerId` is what re-books the occurrence under the new doctor.
 */

interface RecurrenceReselectDoctorViewProps {
  organizationId: string;
  practiceId: string;
  practiceName?: string | null;
  locationIds: string[];
  appointmentProfileType?: 'ROUTINE' | 'FOLLOWUP' | null;
  appointmentType?: AppointmentValueSets | null;
  /** Occurrence date (ISO) — seeds the day searched. */
  initialDate: string;
  onPick: (provider: { id: string; name: string }, slot: AppointmentSlot) => void;
  onCancel: () => void;
}

function toDateOnly(iso: string): Date {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d;
}

function fmtDayLabel(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function fmtTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatClockTime(d);
}

function specialityLabel(p: ProviderSlotsResponse): string | undefined {
  return (p.speciality as { code?: { displayName?: string } } | undefined)?.code?.displayName;
}

export function RecurrenceReselectDoctorView({
  organizationId,
  practiceId,
  practiceName,
  locationIds,
  appointmentProfileType,
  appointmentType,
  initialDate,
  onPick,
  onCancel,
}: RecurrenceReselectDoctorViewProps) {
  const [date, setDate] = useState<Date>(() => toDateOnly(initialDate));

  const { data: allProviders = [], isLoading } = useProviderSlots({
    organizationId,
    practiceId,
    date: date.toISOString(),
    locationIds,
    searchSlot: true,
    slotCount: 6,
    appointmentProfileType: appointmentProfileType ?? null,
  });

  // PROD-1024 (F-SCH-132): self-filter for a doctor/nurse — they reselect only
  // themselves. Flutter `select_doctor_widget.dart:112-114` filters doctor-only;
  // we reuse the shared create-flow helper (also covers nurse) for consistency
  // across every create-appointment provider picker.
  const selfFilterUser = useAuthStore((s) => s.user);
  const providers = useMemo(
    () => filterProvidersForCurrentUser(allProviders, selfFilterUser),
    [allProviders, selfFilterUser],
  );

  const shiftDay = (delta: number) => {
    setDate((prev) => {
      const next = new Date(prev);
      next.setDate(next.getDate() + delta);
      return next;
    });
  };

  const pick = (provider: ProviderSlotsResponse, slot: AppointmentSlot) => {
    onPick(
      { id: provider.id, name: provider.name },
      {
        start: slot.start,
        end: slot.end,
        providerId: provider.id,
        practiceId,
        practiceName: practiceName ?? undefined,
        appointmentType: slot.appointmentType ?? appointmentType ?? undefined,
        overbooked: slot.overbooked ?? false,
        comment: slot.comment,
      },
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-default flex-shrink-0">
        <button
          onClick={onCancel}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Back
        </button>
        <h2 className="text-base font-bold text-text-primary">Select another doctor</h2>
        <span className="w-10" aria-hidden />
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide px-4 py-4 space-y-4">
        {/* Day stepper */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => shiftDay(-1)}
            aria-label="Previous day"
            className="h-8 w-8 flex items-center justify-center rounded-full border border-border-default text-brand-primary hover:bg-blue-100"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-semibold text-text-primary">{fmtDayLabel(date)}</span>
          <button
            type="button"
            onClick={() => shiftDay(1)}
            aria-label="Next day"
            className="h-8 w-8 flex items-center justify-center rounded-full border border-border-default text-brand-primary hover:bg-blue-100"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 h-24 text-text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="text-sm">Loading doctors…</span>
          </div>
        ) : providers.length === 0 ? (
          <p className="text-sm text-text-muted text-center py-8">
            No doctors available on this day.
          </p>
        ) : (
          <div className="space-y-3">
            {providers.map((p) => {
              const slots = p.slots ?? [];
              const speciality = specialityLabel(p);
              return (
                <div
                  key={p.id}
                  className="rounded-lg border border-border-default bg-surface-card p-3"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <PatientAvatar
                      name={p.name}
                      photoUrl={p.photo}
                      size="sm"
                      variant="provider"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-text-primary truncate">
                        {p.name}
                      </p>
                      {speciality ? (
                        <p className="text-xs text-text-muted truncate">{speciality}</p>
                      ) : null}
                    </div>
                  </div>
                  {slots.length > 0 ? (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      {slots.map((s, i) => (
                        <button
                          key={`${p.id}-${s.start ?? i}`}
                          type="button"
                          onClick={() => pick(p, s)}
                          className={cn(
                            'h-10 rounded-md text-sm font-medium border border-border-default',
                            'bg-surface-card text-text-primary hover:border-brand-primary hover:bg-blue-100 hover:text-brand-primary transition-colors',
                          )}
                        >
                          {fmtTime(s.start)}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-2 text-xs italic text-text-muted">
                      No slots on this day.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
